import { useQuery } from '@tanstack/react-query';
import {
  BASE_CURRENCY,
  currencySymbol,
  isForeignCurrency,
  toBase,
  type ExchangeRateView,
} from '@goyone/shared';
import { api, qs } from './api';
import { money } from './format';

/**
 * العملات في الواجهة.
 *
 * الدفاتر بالجنيه، والخزينة أو الحساب بعملة أجنبية يُعرض رصيده بعملته وبجواره
 * ما يساويه بالجنيه بسعر اليوم.
 */

export { BASE_CURRENCY, currencySymbol, isForeignCurrency, toBase };

/** مبلغ بعملة معيّنة برمزها — «12,345 $». بلا عملة فهو بالجنيه. */
export function fxMoney(value: unknown, currency?: string | null): string {
  return money(value, currencySymbol(currency ?? BASE_CURRENCY));
}

const rateFmt = new Intl.NumberFormat('ar-EG-u-nu-latn', { maximumFractionDigits: 4 });

/** سعر الصرف للعرض — حتى أربع خانات. */
export function rateText(rate: unknown): string {
  const n = Number(rate);
  return Number.isFinite(n) && n > 0 ? rateFmt.format(n) : '—';
}

/** «≈ X ج.م بسعر اليوم (السعر)» — مؤشر التحويل بجوار رصيد بعملة أجنبية. */
export function baseHint(amount: unknown, rate: unknown, label = 'بسعر اليوم'): string {
  const r = Number(rate);
  if (!Number.isFinite(r) || r <= 0) return 'لا يوجد سعر صرف محفوظ';
  return `≈ ${money(toBase(Number(amount ?? 0), r))} ${label} (${rateText(r)})`;
}

export interface RatesResponse {
  base: string;
  date: string;
  items: ExchangeRateView[];
}

/** أسعار كل العملات في تاريخ (اليوم إن لم يُحدَّد). */
export function useRates(date?: string, enabled = true) {
  return useQuery({
    queryKey: ['exchange-rates', date ?? 'today'],
    queryFn: () => api<RatesResponse>(`/finance/exchange-rates${qs({ date })}`),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** سعر عملة من رد الأسعار — 1 للجنيه، وnull إن لم يُعرف. */
export function rateOf(rates: RatesResponse | undefined, currency: string | null | undefined): number | null {
  if (!isForeignCurrency(currency)) return 1;
  return rates?.items.find((i) => i.currency === currency)?.rate ?? null;
}
